import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { IColumnConfigTableResponsive } from 'src/app/domain/IColumnConfigTableResponsive';
import { FieldTypeEnum } from 'src/app/domain/registration/enums/field-type.enum';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { KeyboardService } from 'src/app/shared/components/keyboard/keyboard.service';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { ToastPositionEnum } from 'src/app/shared/services/toast/toast-position.enum';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { S3_URL_BASE } from 'src/environments/environment';
import { NotificationService } from '../notification.service';
import { COLUMNS_TO_SHOW_WEB, TABLE_CONFIG_COLUMNS } from './notification-settings-config';
import { forkJoin } from 'rxjs';

export interface PeriodicElement {
  allow: boolean;
  module: string;
  sendToPartner: boolean;
  sendToCustomer: boolean;
  sendToUser: boolean;
  emailEnabled: boolean;
  availableTemplates: string[];
  selectedTemplate?: string;
  customTemplate?: string;
}

@Component({
  selector: 'app-notification-settings',
  templateUrl: './notification-settings.component.html',
  styleUrls: ['./notification-settings.component.sass'],
})
export class NotificationSettingsComponent implements OnInit {
  @Output() changePage: EventEmitter<any> = new EventEmitter<any>();
  @Input('hidePagination') hidePagination: boolean;
  @Input() hasFilter: boolean = true;
  @Input('hideFilter') hideFilter: boolean;

  configColumns = TABLE_CONFIG_COLUMNS;
  displayedColumns: string[] = COLUMNS_TO_SHOW_WEB;
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  params: any = {
    getRelation: 1,
    limit: 100,
    page: 0,
    filter: '',
    onlyActives: 0,
  };
  length: number = 0;
  indexSelect: number = -1;
  elementSelected: any;
  data = [];

  pageSize = 100;
  pageSizeOptions: number[] = [100, 150, 200, 500, 1000];
  applied: boolean = false;

  keyboardChange: EventEmitter<any> = new EventEmitter<any>();
  element: any;
  title: string = '';

  constructor(
    public dialog: MatDialog,
    private service: NotificationService,
    public dialogRef: MatDialogRef<NotificationSettingsComponent>,
    private toastService: ToastService,
    public keyboardService: KeyboardService,
    @Inject(MAT_DIALOG_DATA)
    data: { type: string; select: boolean; element: any }
  ) {
    this.element = data.element;
    this.title = `${this.element.name}`;
    // Se necessário, ajuste os parâmetros para buscar a configuração específica
    this.params = { ...this.params, userGroup: this.element.id };
  }

  ngOnInit(): void {
    this.keyboardChange.subscribe((event) => {
      this.applyFilter(Object.assign({ target: { value: event } }));
    });

    this.dialogRef.beforeClosed().subscribe(() =>
      this.dialogRef.close({ element: { applied: this.applied } })
    );
  }

  ngAfterViewInit(): void {
    this.loadList();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.params.filter = filterValue?.toUpperCase();
    this.loadList();
  }

  public pageEvent(event: PageEvent): PageEvent {
    this.params = { ...this.params, page: event.pageIndex, limit: event.pageSize };
    this.loadList();
    return event;
  }

  public setPageSizeOptions(setPageSizeOptionsInput: string) {
    if (setPageSizeOptionsInput)
      this.pageSizeOptions = setPageSizeOptionsInput.split(',').map((str) => +str);
  }

  loadList() {
    // Carrega os módulos (supondo que getNotificationModules retorne o array correto)
    this.service.getNotificationModules().subscribe((modules: any[]) => {
      this.dataSource = new MatTableDataSource<PeriodicElement>(modules);
      this.length = modules.length;
    });
  }

  public isAllSelected() {
    const numSelected = this.dataSource.data.filter((item) => item.allow).length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAll(field: string, value: boolean) {
    this.dataSource.data.forEach((item: any) => {
      item[field] = value;
    });
  }

  isAllSelectedFor(field: string): boolean {
    return this.dataSource.data.length > 0 && this.dataSource.data.every(item => item[field] === true);
  }

  get fieldTypeEnum() {
    return FieldTypeEnum;
  }

  public getUrl(value: string) {
    return `${S3_URL_BASE}${value}`;
  }

  triggerFileInput(element: PeriodicElement) {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.html';
    fileInput.style.display = 'none';
    fileInput.addEventListener('change', (event: any) => this.onFileSelected(event, element));
    document.body.appendChild(fileInput);
    fileInput.click();
  }

  onFileSelected(event: any, element: PeriodicElement) {
    const file: File = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('template', file, file.name);

      // Utiliza o método do NotificationService para realizar o upload
      this.service.uploadTemplate(formData).subscribe(response => {
        // Atualiza o template selecionado para o registro
        element.selectedTemplate = response.templateUrl;
        element.customTemplate = response.templateUrl; // se desejar persistir
        this.toastService.showSuccess('Template customizado adicionado com sucesso!', { timer: 3000 });
      }, error => {
        this.toastService.showError('Erro no upload do template.', { timer: 3000 });
      });
    }
  }

  confirm() {
    const updates = this.dataSource.data.map((config: PeriodicElement) => ({
      module: config.module,
      sendToPartner: config.sendToPartner,
      sendToCustomer: config.sendToCustomer,
      sendToUser: config.sendToUser,
      emailEnabled: config.emailEnabled,
      customTemplate: config.customTemplate || ''
    }));

    this.service.updateNotificationSettingsBatch(updates).subscribe(() => {
      this.toastService.showSuccess('Notificações atualizadas com sucesso!');
      this.dialogRef.close({ applied: true });
    }, (error: any) => {
      this.toastService.showError('Erro ao atualizar configurações', { timer: 3000 });
    });
  }

  public hasValue() {
    return this.dataSource.data.filter((item) => item.allow).length > 0;
  }

  public toggle(row: PeriodicElement) {
    row.allow = !row.allow;
  }
}
